// src/components/Dashboard.js
import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';

const DashboardWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  color: #0C2340;
  text-align: center;
`;

const Heading = styled.div`
  line-height: 1.6;
  padding: 10px 20px;
`;

const ServiceList = styled.ul`
  list-style-type: none;
  padding: 0;
`;

const ServiceItem = styled.li`
  background-color: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 5px;
  margin-bottom: 10px;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ServiceDescription = styled.span`
  padding: 5px 15px;
`;

const ServiceName = styled.span`
  color: #0C2340;
  font-weight: bold;
`;

const DeleteButton = styled.button`
  background-color: #ff4136;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  &:hover {
    background-color: #dc352a;
  }
`;

const Form = styled.form`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const TextArea = styled.textarea`
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  height: 100px;
`;

const SubmitButton = styled.button`
  background-color: #00843D;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  &:hover {
    background-color: #006331;
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  background-color: ${props => props.active ? '#0C2340' : '#f8f8f8'};
  color: ${props => props.active ? 'white' : '#0C2340'};
  border: 1px solid #0C2340;
  border-radius: 4px;
  padding: 10px 20px;
  margin: 0 5px;
  cursor: pointer;
  &:hover {
    background-color: ${props => props.active ? '#0C2340' : '#e8e8e8'};
  }
`;

const ContactList = styled.ul`
  list-style-type: none;
  padding: 0;
`;

const ContactItem = styled.li`
  background-color: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 5px;
  margin-bottom: 10px;
  padding: 15px;
  cursor: pointer;
  &:hover {
    background-color: #e8e8e8;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  max-width: 500px;
  width: 100%;
`;

const CloseButton = styled.button`
  background-color: #0C2340;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  float: right;
  &:hover {
    background-color: #091829;
  }
`;

const StatusSelect = styled.select`
  padding: 5px;
  margin-left: 10px;
  border-radius: 4px;
`;

const ConfirmationModal = styled(Modal)`
  background-color: rgba(0, 0, 0, 0.7);
`;

const ConfirmationContent = styled(ModalContent)`
  text-align: center;
`;

const ConfirmButton = styled(SubmitButton)`
  margin-right: 10px;
`;

const CancelButton = styled(CloseButton)`
  float: none;
`;
const Dashboard = () => {
  const [services, setServices] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [newService, setNewService] = useState({ name: '', description: '' });
  const [activeTab, setActiveTab] = useState('services');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const navigate = useNavigate();

  const fetchServices = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      if (error.response && error.response.status === 401) {
        navigate('/');
      }
    }
  }, [navigate]);

  const fetchContactSubmissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactSubmissions(response.data.filter(submission => submission.status !== 'Delete'));
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
      if (error.response && error.response.status === 401) {
        navigate('/');
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchServices();
    fetchContactSubmissions();
  }, [fetchServices, fetchContactSubmissions]);

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/services`, newService, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewService({ name: '', description: '' });
      fetchServices();
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleDeleteService = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    if (newStatus === 'Delete') {
      setSubmissionToDelete(submissionId);
      setShowDeleteConfirmation(true);
    } else {
      await updateSubmissionStatus(submissionId, newStatus);
    }
  };

  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${process.env.REACT_APP_API_URL}/contact/${submissionId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchContactSubmissions();
    } catch (error) {
      console.error('Error updating submission status:', error);
    }
  };

  const handleDeleteConfirmation = async () => {
    await updateSubmissionStatus(submissionToDelete, 'Delete');
    setShowDeleteConfirmation(false);
    setSubmissionToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setSubmissionToDelete(null);
  };

  const handleSubmissionClick = (submission) => {
    setSelectedSubmission(submission);
  };

  const closeModal = () => {
    setSelectedSubmission(null);
  };

  return (
    <DashboardWrapper>
      <Title>Dashboard</Title>
      <TabContainer>
        <Tab active={activeTab === 'services'} onClick={() => setActiveTab('services')}>Services</Tab>
        <Tab active={activeTab === 'contacts'} onClick={() => setActiveTab('contacts')}>Contact Submissions</Tab>
      </TabContainer>
      {activeTab === 'services' && (
        <>
          <ServiceList>
            {services.map((service) => (
              <ServiceItem key={service.id}>
                <ServiceName>{service.name}</ServiceName>
                <ServiceDescription>{service.description}</ServiceDescription>
                <DeleteButton onClick={() => handleDeleteService(service.id)}>Delete</DeleteButton>
              </ServiceItem>
            ))}
          </ServiceList>
          <Form onSubmit={handleAddService}>
            <Input
              type="text"
              placeholder="Service Name"
              value={newService.name}
              onChange={(e) => setNewService({ ...newService, name: e.target.value })}
              required
            />
            <TextArea
              placeholder="Service Description"
              value={newService.description}
              onChange={(e) => setNewService({ ...newService, description: e.target.value })}
              required
            />
            <SubmitButton type="submit">Add Service</SubmitButton>
          </Form>
        </>
      )}
      {activeTab === 'contacts' && (
        <ContactList>
        <Heading>Click a contact to open more details...</Heading>
        {contactSubmissions.map((submission) => (
          <ContactItem key={submission.id}>
            <div onClick={() => handleSubmissionClick(submission)}>
              {submission.name} - {submission.email}
            </div>
            <StatusSelect
              value={submission.status || 'New'}
              onChange={(e) => handleStatusChange(submission.id, e.target.value)}
            >
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Read">Read</option>
              <option value="Delete">Delete</option>
            </StatusSelect>
          </ContactItem>
        ))}
      </ContactList>
      )}
      {selectedSubmission && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={closeModal}>Close</CloseButton>
            <h2>Contact Submission Details</h2>
            <p><strong>Name:</strong> {selectedSubmission.name}</p>
            <p><strong>Email:</strong> {selectedSubmission.email}</p>
            <p><strong>Message:</strong> {selectedSubmission.message}</p>
            <p><strong>Submitted At:</strong> {new Date(selectedSubmission.submittedAt).toLocaleString()}</p>
            <p><strong>Status:</strong> {selectedSubmission.status || 'New'}</p>
          </ModalContent>
        </Modal>
      )}
      {showDeleteConfirmation && (
        <ConfirmationModal>
          <ConfirmationContent>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete this contact submission?</p>
            <ConfirmButton onClick={handleDeleteConfirmation}>Confirm</ConfirmButton>
            <CancelButton onClick={handleCancelDelete}>Cancel</CancelButton>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </DashboardWrapper>
  );
};

export default Dashboard;